

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    Autocomplete,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewLoyaltySummaryModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        customer_id: '',
        balance_points: 0,
        lifetime_earned: 0,
        lifetime_redeemed: 0,
        lifetime_expired: 0,
        tier_anniversary: '',
        tier_progress_points: '',
        last_earned_on: '',
        last_redeemed_on: ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [customers, setCustomers] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchCustomers();
        }
    }, [isOpen]);

    const fetchCustomers = async () => {
        setLoadingCustomers(true);
        try {
            const response = await axiosInstance.get('/customers/');
            setCustomers(response.data || []);
        } catch (error) {
            console.error('Error fetching customers:', error);
        } finally {
            setLoadingCustomers(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Name is required';
        if (!formData.customer_id) errors.customer_id = 'Customer is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                customer_id: parseInt(formData.customer_id),
                balance_points: formData.balance_points ? parseInt(formData.balance_points) : 0,
                lifetime_earned: formData.lifetime_earned ? parseInt(formData.lifetime_earned) : 0,
                lifetime_redeemed: formData.lifetime_redeemed ? parseInt(formData.lifetime_redeemed) : 0,
                lifetime_expired: formData.lifetime_expired ? parseInt(formData.lifetime_expired) : 0,
                tier_progress_points: formData.tier_progress_points ? parseInt(formData.tier_progress_points) : null,
                tier_anniversary: formData.tier_anniversary || null,
                last_earned_on: formData.last_earned_on || null,
                last_redeemed_on: formData.last_redeemed_on || null,
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
            setFormData({
                name: '',
                customer_id: '',
                balance_points: 0,
                lifetime_earned: 0,
                lifetime_redeemed: 0,
                lifetime_expired: 0,
                tier_anniversary: '',
                tier_progress_points: '',
                last_earned_on: '',
                last_redeemed_on: ''
            });
        } catch (error) {
            console.error('Error saving Loyalty Summary:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Loyalty Summary',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '80%', md: '700px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Loyalty Summary</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={customers}
                                        getOptionLabel={(option) => option?.name || ''}
                                        loading={loadingCustomers}
                                        value={customers.find(c => c.id === parseInt(formData.customer_id)) || null}
                                        onChange={(e, newValue) => {
                                            setFormData(prev => ({ ...prev, customer_id: newValue?.id || '' }));
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: '35px' } }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Customer"
                                                variant="outlined"
                                                margin="normal"
                                                required
                                                error={!!formErrors.customer_id}
                                                helperText={formErrors.customer_id}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadingCustomers ? <CircularProgress size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Balance Points"
                                        name="balance_points"
                                        type="number"
                                        value={formData.balance_points}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Lifetime Earned"
                                        name="lifetime_earned"
                                        type="number"
                                        value={formData.lifetime_earned}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Lifetime Redeemed"
                                        name="lifetime_redeemed"
                                        type="number"
                                        value={formData.lifetime_redeemed}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Lifetime Expired"
                                        name="lifetime_expired"
                                        type="number"
                                        value={formData.lifetime_expired}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Tier Progress Points"
                                        name="tier_progress_points"
                                        type="number"
                                        value={formData.tier_progress_points}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Tier Anniversary"
                                        name="tier_anniversary"
                                        type="date"
                                        value={formData.tier_anniversary}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Last Earned On"
                                        name="last_earned_on"
                                        type="date"
                                        value={formData.last_earned_on}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Last Redeemed On"
                                        name="last_redeemed_on"
                                        type="date"
                                        value={formData.last_redeemed_on}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Loyalty Summary'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewLoyaltySummaryModal;

